import React, { createContext, useContext, useMemo } from 'react';
import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from '@apollo/client';
import { useKeycloakAuth } from './KeycloakAuthProvider';
import { EnhancedAgentApiService } from '../services/api/agent-api';

/**
 * Unified API Context for Finbot
 *
 * This context provides a single source of truth for all API calls, consolidating:
 * - Knowledge Graph API operations (document KG building, graph data, metrics)
 * - KG Build API operations (folder tree, build jobs, progress tracking)
 * - Agent KG API operations (agent-specific KG operations, status, metrics)
 * - Apollo GraphQL client for database operations
 *
 * All services automatically inject authentication tokens and use consistent
 * error handling and base URL configuration.
 *
 * Legacy service files (agentKGApi.ts, kgBuildApi.ts, knowledgeGraphApi.ts)
 * have been removed - all functionality is now available through this context.
 */

// Import unified API types from proper type files
import {
  KGBuildStatus,
  KGMetrics as KGApiMetrics,
  KGGraphData,
  KGJobResponse,
  KGImportValidationResult,
  KGImportJobStatus,
  KGExportRequest,
  KGExportResponse,
  KGNode
} from '../types/knowledgeGraph';

import { KGJob, FolderNode, KGBuildRequest as KGBuildApiRequest, KGBuildMetrics } from '../types/kgBuild';
import { KGPipelineJob } from '../types/kgPipeline';

import { AgentKGStatus, AgentKGBuildRequest, AgentKGBuildResponse, AgentKGMetrics, AgentKGReadinessResult, AgentKGBuildHistoryItem } from '../types/agentKG';

import { throwIfNotOk } from '../utils/apiError';
import { ParsedDocumentUploadResponse } from '../types/upload';

export { ApiError } from '../utils/apiError';
export type { ApiErrorData } from '../utils/apiError';

// Enhanced Knowledge Graph API service class with automatic auth injection
class EnhancedKnowledgeGraphApiService {
  private baseUrl: string;
  private getAccessToken: () => string | undefined;

  constructor(getAccessToken: () => string | undefined) {
    this.baseUrl = this.getPythonBackendUrl();
    this.getAccessToken = getAccessToken;
  }

  private getPythonBackendUrl(): string {
    if (process.env.REACT_APP_PYTHON_BACKEND_URL) {
      return process.env.REACT_APP_PYTHON_BACKEND_URL;
    }

    if (process.env.NODE_ENV === 'development') {
      return '';
    }

    return '';
  }

  private async fetchWithAuth(endpoint: string, options: RequestInit = {}) {
    const token = this.getAccessToken();

    const response = await fetch(`${this.baseUrl}/api${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers
      }
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(`KG API call failed: ${response.status} ${errorData}`);
    }

    return response.json();
  }

  // All the same methods as the original service
  async buildDocumentKG(documentIds: string[]): Promise<KGJobResponse> {
    return this.fetchWithAuth(`/knowledge-graph/build/documents`, {
      method: 'POST',
      body: JSON.stringify({ document_ids: documentIds })
    });
  }

  async getBuildStatus(jobId: string): Promise<KGBuildStatus> {
    return this.fetchWithAuth(`/knowledge-graph/jobs/${jobId}`);
  }

  async buildBatchKG(documentIds: string[]): Promise<KGJobResponse> {
    return this.buildDocumentKG(documentIds);
  }

  async getMetrics(): Promise<KGApiMetrics> {
    // Always pass namespace for Finbot
    return this.fetchWithAuth(`/knowledge-graph/metrics?namespace=finbot`);
  }

  // KG Management endpoints for status and composition
  async getKGStatus(namespace: string = process.env.REACT_APP_KG_NAMESPACE || 'finbot'): Promise<Record<string, unknown>> {
    return this.fetchWithAuth(`/knowledge-graph/status/${namespace}`);
  }

  async getKGComposition(namespace: string = process.env.REACT_APP_KG_NAMESPACE || 'finbot'): Promise<Record<string, unknown>> {
    return this.fetchWithAuth(`/knowledge-graph/composition/${namespace}`);
  }

  // Get database validation results specifically
  async validateDatabase(namespace: string = process.env.REACT_APP_KG_NAMESPACE || 'finbot'): Promise<Record<string, unknown>> {
    return this.fetchWithAuth(`/knowledge-graph/validate/database/${namespace}`);
  }

  async getGraphData(filters?: {
    documentTypes?: string[];
    limit?: number;
    includeChildren?: boolean;
    nodeIds?: string[];
    depth?: number;
    sampleSize?: number;
    rootsOnly?: boolean;
  }): Promise<KGGraphData> {
    const params = new URLSearchParams();

    // Always pass namespace for finbot
    params.set('namespace', 'finbot');

    if (filters?.documentTypes?.length) {
      params.set('node_types', filters.documentTypes.join(','));
    }
    if (filters?.limit) {
      params.set('limit', filters.limit.toString());
    }
    if (filters?.includeChildren !== undefined) {
      params.set('include_children', filters.includeChildren.toString());
    }

    // New parameters for professional KG rendering
    if (filters?.nodeIds?.length) {
      params.set('node_ids', filters.nodeIds.join(','));
    }
    if (filters?.depth !== undefined && filters.depth > 0) {
      params.set('depth', filters.depth.toString());
    }
    if (filters?.sampleSize !== undefined) {
      params.set('sample_size', filters.sampleSize.toString());
    }
    if (filters?.rootsOnly) {
      params.set('roots_only', 'true');
    }

    const queryString = params.toString();
    const endpoint = `/knowledge-graph/data?${queryString}`;

    return this.fetchWithAuth(endpoint);
  }

  async cancelJob(jobId: string): Promise<{ success: boolean; message: string }> {
    return this.fetchWithAuth(`/knowledge-graph/jobs/${jobId}`, {
      method: 'DELETE'
    });
  }

  async getStatus(): Promise<{ status: string; message: string }> {
    return this.fetchWithAuth(`/knowledge-graph/status`);
  }

  async searchNodes(
    query: string,
    filters?: {
      documentTypes?: string[];
      limit?: number;
    }
  ): Promise<KGNode[]> {
    const params = new URLSearchParams({ q: query });
    if (filters?.documentTypes?.length) {
      params.set('document_types', filters.documentTypes.join(','));
    }
    if (filters?.limit) {
      params.set('limit', filters.limit.toString());
    }

    return this.fetchWithAuth(`/knowledge-graph/search?${params.toString()}`);
  }

  async getChildNodes(parentId: string): Promise<KGGraphData> {
    return this.fetchWithAuth(`/knowledge-graph/data?parent_id=${parentId}`);
  }

  // Import/Export methods

  /**
   * Validate KG import using chunked upload for a single kg.pkl file.
   *
   * @param kgFile - The unified kg.pkl file (streaming pickle: meta + nodes + relationships)
   * @param onProgress - Optional progress callback (phase: 'uploading' | 'validating', progress: 0-100)
   * @returns Validation result with validation_id if successful
   */
  async validateImportChunked(
    kgFile: File,
    onProgress?: (phase: 'uploading' | 'validating', progress: number) => void
  ): Promise<KGImportValidationResult> {
    const namespace = 'finbot';
    const CHUNK_SIZE = 10 * 1024 * 1024; // 10MB chunks
    const token = this.getAccessToken();

    if (!token) {
      throw new Error('Authentication required for chunked validation');
    }

    const chunkCount = Math.ceil(kgFile.size / CHUNK_SIZE);

    // Initialize upload session
    const initResponse = await fetch(`${this.baseUrl}/api/knowledge-graph/import/validate/chunked/init`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        filename: kgFile.name,
        file_size: kgFile.size,
        chunk_count: chunkCount,
        namespace
      })
    });

    if (!initResponse.ok) {
      throw new Error(`Failed to initialize upload: ${initResponse.status}`);
    }

    const { upload_id } = await initResponse.json();

    // Upload chunks
    for (let i = 0; i < chunkCount; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, kgFile.size);
      const chunk = kgFile.slice(start, end);

      const chunkData = await this.fileToBase64(chunk);

      const chunkResponse = await fetch(`${this.baseUrl}/api/knowledge-graph/import/validate/chunked/chunk`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          upload_id,
          chunk_index: i,
          chunk_data: chunkData,
          is_last_chunk: i === chunkCount - 1
        })
      });

      if (!chunkResponse.ok) {
        throw new Error(`Failed to upload chunk ${i + 1}/${chunkCount}: ${chunkResponse.status}`);
      }

      onProgress?.('uploading', ((i + 1) / chunkCount) * 100);
    }

    // Complete and validate
    onProgress?.('validating', 100);

    const completeResponse = await fetch(`${this.baseUrl}/api/knowledge-graph/import/validate/chunked/complete`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      body: JSON.stringify({
        kg_upload_id: upload_id
      })
    });

    if (!completeResponse.ok) {
      const errorData = await completeResponse.text();
      throw new Error(`Chunked validation failed: ${completeResponse.status} ${errorData}`);
    }

    const validationResult = await completeResponse.json();

    console.log('[ApiContext] kg.pkl validation API response:', {
      status: completeResponse.status,
      ok: completeResponse.ok,
      result: validationResult
    });

    return validationResult;
  }

  /**
   * Helper method to convert File/Blob to base64 string.
   */
  private fileToBase64(file: File | Blob): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const result = reader.result as string;
        // Remove data URL prefix (e.g., "data:application/octet-stream;base64,")
        const base64 = result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  async executeImport(
    validationId: string,
    agentConfig?: {
      name: string;
      languageId?: string;
      folderIds?: string[];
      tools?: Record<string, unknown>[];
      workflowDefinition?: Record<string, unknown>;
    }
  ): Promise<KGJobResponse> {
    const requestBody: {
      validation_id: string;
      import_options: {
        agent_config?: {
          name: string;
          language_id?: string;
          tools: Record<string, unknown>[];
          workflow_definition: Record<string, unknown> | null;
        };
        folder_ids?: string[];
      };
    } = {
      validation_id: validationId,
      import_options: {}
    };

    if (agentConfig) {
      requestBody.import_options.agent_config = {
        name: agentConfig.name,
        language_id: agentConfig.languageId,
        tools: agentConfig.tools || [],
        workflow_definition: agentConfig.workflowDefinition || null
      };
      // folder_ids must be at top level of import_options (not inside agent_config)
      requestBody.import_options.folder_ids = agentConfig.folderIds || [];
    }

    return this.fetchWithAuth('/knowledge-graph/import/execute', {
      method: 'POST',
      body: JSON.stringify(requestBody)
    });
  }

  async getImportJobStatus(jobId: string, signal?: AbortSignal): Promise<KGImportJobStatus> {
    return this.fetchWithAuth(`/knowledge-graph/import/status/${jobId}`, {
      ...(signal && { signal }),
      cache: 'no-cache' as RequestCache
    });
  }

  async exportKnowledgeGraph(options: KGExportRequest = {}): Promise<KGExportResponse> {
    return this.fetchWithAuth('/knowledge-graph/export', {
      method: 'POST',
      body: JSON.stringify(options)
    });
  }

  async downloadExport(exportId: string): Promise<Blob> {
    const token = this.getAccessToken();

    const response = await fetch(`${this.baseUrl}/api/knowledge-graph/export/${exportId}`, {
      headers: {
        ...(token && { Authorization: `Bearer ${token}` })
      }
    });

    if (!response.ok) {
      throw new Error(`Export download failed: ${response.status}`);
    }

    return response.blob();
  }

  async rebuildRelationships(): Promise<KGJobResponse> {
    return this.fetchWithAuth('/knowledge-graph/build/all', {
      method: 'POST',
      body: JSON.stringify({ force_rebuild: true })
    });
  }

  async updateKG(namespace: string, documentIds?: string[]): Promise<KGJobResponse> {
    return this.fetchWithAuth('/knowledge-graph/update', {
      method: 'POST',
      body: JSON.stringify({ namespace, ...(documentIds?.length && { document_ids: documentIds }) })
    });
  }

  async getUpdateJobStatus(jobId: string): Promise<KGJobResponse> {
    return this.fetchWithAuth(`/knowledge-graph/jobs/${jobId}`);
  }

  async getFolderKGStatus(folderId: string): Promise<Record<string, unknown>> {
    return this.fetchWithAuth(`/knowledge-graph/folders/${folderId}/kg-status`);
  }

  async getImportHistory<T = Record<string, unknown>>(): Promise<T[]> {
    return this.fetchWithAuth('/knowledge-graph/import/history');
  }

  async cancelImportJob(jobId: string): Promise<Record<string, unknown>> {
    return this.fetchWithAuth(`/knowledge-graph/import/${jobId}/cancel`, {
      method: 'POST'
    });
  }

  async previewOperation(operationType: string, userId: string, namespace: string, options: Record<string, unknown> = {}): Promise<Record<string, unknown>> {
    return this.fetchWithAuth('/knowledge-graph/preview-operation', {
      method: 'POST',
      body: JSON.stringify({
        operation_type: operationType,
        user_id: userId,
        namespace,
        options
      })
    });
  }

  async getOperationStatus(jobId: string, signal?: AbortSignal): Promise<Record<string, unknown>> {
    return this.fetchWithAuth(`/knowledge-graph/operation-status/${jobId}`, { signal });
  }

  async cancelKGOperation(jobId: string): Promise<Record<string, unknown>> {
    return this.fetchWithAuth(`/knowledge-graph/cancel-operation/${jobId}`, {
      method: 'POST'
    });
  }

  async safeDelete(namespace: string, body: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.fetchWithAuth(`/knowledge-graph/safe-delete/${namespace}`, {
      method: 'POST',
      body: JSON.stringify(body)
    });
  }

  async buildDocumentsWithOptions(body: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.fetchWithAuth('/knowledge-graph/build/documents', {
      method: 'POST',
      body: JSON.stringify(body)
    });
  }

  async exportWithOptions(body: Record<string, unknown>): Promise<Record<string, unknown>> {
    return this.fetchWithAuth('/knowledge-graph/export', {
      method: 'POST',
      body: JSON.stringify(body)
    });
  }
}

// Enhanced KG Build API service class
class EnhancedKGBuildApiService {
  private baseUrl: string;
  private getAccessToken: () => string | undefined;

  constructor(getAccessToken: () => string | undefined) {
    this.baseUrl = this.getPythonBackendUrl();
    this.getAccessToken = getAccessToken;
  }

  private getPythonBackendUrl(): string {
    if (process.env.REACT_APP_PYTHON_BACKEND_URL) {
      return process.env.REACT_APP_PYTHON_BACKEND_URL;
    }

    if (process.env.NODE_ENV === 'development') {
      return '';
    }

    return '';
  }

  private async fetchWithAuth(endpoint: string, options: RequestInit = {}) {
    const token = this.getAccessToken();

    const response = await fetch(`${this.baseUrl}/api${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers
      }
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(`KG Build API call failed: ${response.status} ${errorData}`);
    }

    return response.json();
  }

  async getFolderTree(): Promise<FolderNode[]> {
    const response = await this.fetchWithAuth('/knowledge-graph/folders/tree');
    return response.folders || [];
  }

  async buildFolderKG(request: KGBuildApiRequest): Promise<{ job_id: string }> {
    return this.fetchWithAuth('/knowledge-graph/build/folder', {
      method: 'POST',
      body: JSON.stringify(request)
    });
  }

  async buildDocumentKG(documentIds: string[]): Promise<{ job_id: string }> {
    return this.fetchWithAuth('/knowledge-graph/build/documents', {
      method: 'POST',
      body: JSON.stringify({ document_ids: documentIds })
    });
  }

  async getJobStatus(jobId: string): Promise<KGJob> {
    return this.fetchWithAuth(`/knowledge-graph/jobs/${jobId}`);
  }

  async cancelJob(jobId: string): Promise<{ success: boolean; message: string }> {
    return this.fetchWithAuth(`/knowledge-graph/jobs/${jobId}`, {
      method: 'DELETE'
    });
  }

  async getUserJobs(): Promise<KGJob[]> {
    const response = await this.fetchWithAuth('/knowledge-graph/jobs');
    return response.jobs || [];
  }

  async getKGMetrics(): Promise<KGBuildMetrics> {
    return this.fetchWithAuth('/knowledge-graph/metrics');
  }
}

// Enhanced Agent KG API service class
class EnhancedAgentKGApiService {
  private baseUrl: string;
  private getAccessToken: () => string | undefined;

  constructor(getAccessToken: () => string | undefined) {
    this.baseUrl = this.getPythonBackendUrl();
    this.getAccessToken = getAccessToken;
  }

  private getPythonBackendUrl(): string {
    if (process.env.REACT_APP_PYTHON_BACKEND_URL) {
      return process.env.REACT_APP_PYTHON_BACKEND_URL;
    }

    if (process.env.NODE_ENV === 'development') {
      return '';
    }

    return '';
  }

  private async fetchWithAuth(endpoint: string, options: RequestInit = {}) {
    const token = this.getAccessToken();

    const response = await fetch(`${this.baseUrl}/api${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers
      }
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(`Agent KG API call failed: ${response.status} ${errorData}`);
    }

    return response.json();
  }

  async getAgentKGStatus(agentId: string): Promise<AgentKGStatus> {
    return this.fetchWithAuth(`/agents/${agentId}/kg-status`);
  }

  async getBatchAgentKGStatus(agentIds: string[]): Promise<Record<string, AgentKGStatus>> {
    // Return empty object for empty agent list
    if (agentIds.length === 0) {
      return {};
    }

    const params = new URLSearchParams();
    agentIds.forEach((id) => params.append('agent_ids', id));

    return this.fetchWithAuth(`/agents/kg-status?${params.toString()}`);
  }

  async buildAgentKG(request: AgentKGBuildRequest): Promise<AgentKGBuildResponse> {
    return this.fetchWithAuth(`/agents/${request.agent_id}/kg-build`, {
      method: 'POST',
      body: JSON.stringify({
        force_rebuild: request.force_rebuild || false
      })
    });
  }

  async cancelAgentKGBuild(agentId: string, jobId: string): Promise<{ success: boolean; message: string }> {
    return this.fetchWithAuth(`/agents/${agentId}/kg-build/${jobId}`, {
      method: 'DELETE'
    });
  }

  async getAgentKGBuildHistory(agentId: string, limit: number = 10): Promise<AgentKGBuildHistoryItem[]> {
    return this.fetchWithAuth(`/agents/${agentId}/kg-build/history?limit=${limit}`);
  }

  async getAgentKGMetrics(agentId: string): Promise<AgentKGMetrics> {
    return this.fetchWithAuth(`/agents/${agentId}/kg-metrics`);
  }

  async testAgentKGReadiness(agentId: string): Promise<AgentKGReadinessResult> {
    return this.fetchWithAuth(`/agents/${agentId}/kg-test`);
  }

  async rebuildAgentKGRelationships(agentId: string): Promise<AgentKGBuildResponse> {
    return this.fetchWithAuth(`/agents/${agentId}/kg-relationships/rebuild`, {
      method: 'POST'
    });
  }
}

// KG Pipeline API service class for S3-based document processing
class KGPipelineApiService {
  private baseUrl: string;
  private getAccessToken: () => string | undefined;

  constructor(getAccessToken: () => string | undefined) {
    this.baseUrl = this.getPythonBackendUrl();
    this.getAccessToken = getAccessToken;
  }

  private getPythonBackendUrl(): string {
    if (process.env.REACT_APP_PYTHON_BACKEND_URL) {
      return process.env.REACT_APP_PYTHON_BACKEND_URL;
    }
    if (process.env.NODE_ENV === 'development') {
      return '';
    }
    return '';
  }

  private async fetchWithAuth(endpoint: string, options: RequestInit = {}) {
    const token = this.getAccessToken();

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers
      }
    });

    if (!response.ok) {
      const errorData = await response.text();
      throw new Error(`KG Pipeline API call failed: ${response.status} ${errorData}`);
    }

    return response.json();
  }

  // Start a new pipeline job
  async startPipeline(inputPath: string, skipAnalysis: boolean = false): Promise<{ jobId: string; status: string; message: string }> {
    return this.fetchWithAuth('/api/kg-pipeline/start', {
      method: 'POST',
      body: JSON.stringify({ input_path: inputPath, skip_analysis: skipAnalysis })
    });
  }

  // Get pipeline job status
  async getJobStatus(jobId: string): Promise<{ job: KGPipelineJob }> {
    return this.fetchWithAuth(`/api/kg-pipeline/jobs/${jobId}`);
  }

  // List user's pipeline jobs
  async listJobs(skip: number = 0, limit: number = 20): Promise<{ jobs: KGPipelineJob[]; total: number }> {
    const params = new URLSearchParams({ skip: skip.toString(), limit: limit.toString() });
    return this.fetchWithAuth(`/api/kg-pipeline/jobs?${params.toString()}`);
  }

  // Cancel a running pipeline job
  async cancelJob(jobId: string): Promise<{ success: boolean; message: string }> {
    return this.fetchWithAuth(`/api/kg-pipeline/jobs/${jobId}/cancel`, {
      method: 'POST'
    });
  }

  // Get latest pipeline job for current user/namespace
  async getLatestJob(): Promise<{ job: KGPipelineJob | null }> {
    return this.fetchWithAuth('/api/kg-pipeline/latest');
  }

  // List objects in an S3 bucket
  async listBucket(
    bucket: string,
    prefix: string = '',
    maxKeys: number = 1000
  ): Promise<{ objects: Array<{ key: string; size: number; last_modified: string; is_folder: boolean }>; prefix: string; bucket: string; total: number }> {
    const params = new URLSearchParams({
      bucket,
      prefix,
      max_keys: maxKeys.toString()
    });
    return this.fetchWithAuth(`/api/s3/list-bucket?${params.toString()}`);
  }

  // S3 presigned URL for object access
  async getPresignedUrl(bucket: string, key: string, expiresIn: number = 3600): Promise<{ url: string; expires_in: number }> {
    const params = new URLSearchParams({
      bucket,
      key,
      expires_in: expiresIn.toString()
    });
    return this.fetchWithAuth(`/api/s3/presigned-url?${params.toString()}`);
  }

  // S3 health check
  async getS3Health(): Promise<{ enabled: boolean; healthy: boolean; endpoint?: string; error?: string }> {
    return this.fetchWithAuth('/api/s3/health');
  }
}

// Enhanced Parsed Upload API service class
class EnhancedParsedUploadApiService {
  private baseUrl: string;
  private getAccessToken: () => string | undefined;

  constructor(getAccessToken: () => string | undefined) {
    this.baseUrl = this.getPythonBackendUrl();
    this.getAccessToken = getAccessToken;
  }

  private getPythonBackendUrl(): string {
    if (process.env.REACT_APP_PYTHON_BACKEND_URL) {
      return process.env.REACT_APP_PYTHON_BACKEND_URL;
    }
    if (process.env.NODE_ENV === 'development') {
      return '';
    }
    return '';
  }

  async uploadArchive(
    archiveBlob: Blob,
    parentFolderId?: string,
    folderName?: string,
  ): Promise<ParsedDocumentUploadResponse> {
    const formData = new FormData();
    formData.append('file', archiveBlob, 'upload.bin');
    if (parentFolderId) {
      formData.append('parent_folder_id', parentFolderId);
    }
    if (folderName) {
      formData.append('folder_name', folderName);
    }

    const token = this.getAccessToken();
    const response = await fetch(`${this.baseUrl}/api/parsed-docs/upload`, {
      method: 'POST',
      headers: {
        ...(token && { Authorization: `Bearer ${token}` })
      },
      body: formData
    });

    await throwIfNotOk(response, 'Upload archive');
    return response.json();
  }
}

// EnhancedAgentApiService extracted to services/api/agent-api.ts to align
// finbot's file layout with the other cluster-C forks (citeka, skhcn, sdl).
// See top-of-file import below.

// Teams API service class (Finbot uses /api/teams/ prefix)
class EnhancedTeamsApiService {
  private getAccessToken: () => string | undefined;

  constructor(getAccessToken: () => string | undefined) {
    this.getAccessToken = getAccessToken;
  }

  private getHeaders(): Record<string, string> {
    const token = this.getAccessToken();
    const headers: Record<string, string> = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  }

  async getIcon(platformAppId: string, iconType: 'color' | 'outline'): Promise<Response> {
    return fetch(`/api/teams/icon/${platformAppId}/${iconType}`, {
      method: 'GET',
      headers: this.getHeaders()
    });
  }

  async getManifest(platformAppId: string): Promise<Record<string, unknown>> {
    const headers = this.getHeaders();
    headers['Content-Type'] = 'application/json';

    const response = await fetch(`/api/teams/preview-manifest/${platformAppId}`, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.json();
  }

  async downloadApp(platformAppId: string): Promise<Blob> {
    const headers = this.getHeaders();
    headers['Content-Type'] = 'application/json';

    const response = await fetch(`/api/teams/download-app/${platformAppId}`, {
      method: 'GET',
      headers
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.blob();
  }
}

// Document API service class
class EnhancedDocumentApiService {
  private getAccessToken: () => string | undefined;

  constructor(getAccessToken: () => string | undefined) {
    this.getAccessToken = getAccessToken;
  }

  async exportStructure(options: { download?: boolean; rootFolderId?: string } = {}): Promise<Record<string, unknown>> {
    const token = this.getAccessToken();
    const params = new URLSearchParams();
    if (options.download) params.set('download', 'true');
    if (options.rootFolderId) params.set('root_folder_id', options.rootFolderId);

    const response = await fetch(`/api/documents/structure/export?${params.toString()}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` })
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to export structure: ${response.statusText}`);
    }

    return response.json();
  }
}

// STT (Speech-to-Text) API service class
class EnhancedSTTApiService {
  private getAccessToken: () => string | undefined;

  constructor(getAccessToken: () => string | undefined) {
    this.getAccessToken = getAccessToken;
  }

  async transcribe(audioBlob: Blob, language: string = 'vi'): Promise<{ text: string }> {
    const token = this.getAccessToken();
    if (!token) {
      throw new Error('Authentication required for transcription');
    }

    const formData = new FormData();
    formData.append('audio', audioBlob, 'recording.wav');
    formData.append('language', language);

    const response = await fetch('/api/stt/transcribe', {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
      body: formData
    });

    if (!response.ok) {
      throw new Error(`STT API call failed: ${response.status}`);
    }

    return response.json();
  }
}

// API Context value interface
interface ApiContextValue {
  apolloClient: ApolloClient<object>;
  kgApi: EnhancedKnowledgeGraphApiService;
  kgBuildApi: EnhancedKGBuildApiService;
  agentKGApi: EnhancedAgentKGApiService;
  kgPipelineApi: KGPipelineApiService;
  parsedUploadApi: EnhancedParsedUploadApiService;
  agentApi: EnhancedAgentApiService;
  teamsApi: EnhancedTeamsApiService;
  documentApi: EnhancedDocumentApiService;
  sttApi: EnhancedSTTApiService;
}

// Create the API context
const ApiContext = createContext<ApiContextValue | undefined>(undefined);

// API Provider component
export const ApiProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { getAccessToken } = useKeycloakAuth();

  // Create Apollo Client with automatic auth injection
  const apolloClient = useMemo(() => {
    const httpLink = new HttpLink({
      uri: process.env.REACT_APP_GRAPHQL_ENDPOINT || '/graphql'
    });

    const authMiddleware = new ApolloLink((operation, forward) => {
      const token = getAccessToken();

      operation.setContext(({ headers = {} }) => ({
        headers: {
          ...headers,
          authorization: token ? `Bearer ${token}` : ''
        }
      }));

      return forward(operation);
    });

    const link = authMiddleware.concat(httpLink);

    return new ApolloClient({
      link: link,
      cache: new InMemoryCache({
        typePolicies: {
          Query: {
            fields: {
              findManyMessages: {
                merge(_existing = [], incoming = []) {
                  return incoming;
                }
              }
            }
          }
        }
      }),
      defaultOptions: {
        watchQuery: {
          fetchPolicy: 'network-only',
          errorPolicy: 'ignore'
        },
        query: {
          fetchPolicy: 'network-only',
          errorPolicy: 'all'
        }
      }
    });
  }, [getAccessToken]);

  // Create API services with automatic auth injection
  const kgApi = useMemo(() => {
    return new EnhancedKnowledgeGraphApiService(() => getAccessToken() || undefined);
  }, [getAccessToken]);

  const kgBuildApi = useMemo(() => {
    return new EnhancedKGBuildApiService(() => getAccessToken() || undefined);
  }, [getAccessToken]);

  const agentKGApi = useMemo(() => {
    return new EnhancedAgentKGApiService(() => getAccessToken() || undefined);
  }, [getAccessToken]);

  const kgPipelineApi = useMemo(() => {
    return new KGPipelineApiService(() => getAccessToken() || undefined);
  }, [getAccessToken]);

  const parsedUploadApi = useMemo(() => {
    return new EnhancedParsedUploadApiService(() => getAccessToken() || undefined);
  }, [getAccessToken]);

  const agentApi = useMemo(() => {
    return new EnhancedAgentApiService(() => getAccessToken() || undefined);
  }, [getAccessToken]);

  const teamsApi = useMemo(() => {
    return new EnhancedTeamsApiService(() => getAccessToken() || undefined);
  }, [getAccessToken]);

  const documentApi = useMemo(() => {
    return new EnhancedDocumentApiService(() => getAccessToken() || undefined);
  }, [getAccessToken]);

  const sttApi = useMemo(() => {
    return new EnhancedSTTApiService(() => getAccessToken() || undefined);
  }, [getAccessToken]);

  const value = useMemo(
    () => ({
      apolloClient,
      kgApi,
      kgBuildApi,
      agentKGApi,
      kgPipelineApi,
      parsedUploadApi,
      agentApi,
      teamsApi,
      documentApi,
      sttApi
    }),
    [apolloClient, kgApi, kgBuildApi, agentKGApi, kgPipelineApi, parsedUploadApi, agentApi, teamsApi, documentApi, sttApi]
  );

  return <ApiContext.Provider value={value}>{children}</ApiContext.Provider>;
};

// Custom hook to access API services
export const useApi = () => {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useApi must be used within ApiProvider');
  }
  return context;
};

// Individual service hooks for convenience
export const useKnowledgeGraphApi = () => {
  const { kgApi } = useApi();
  return kgApi;
};

export const useKGBuildApi = () => {
  const { kgBuildApi } = useApi();
  return kgBuildApi;
};

export const useAgentKGApi = () => {
  const { agentKGApi } = useApi();
  return agentKGApi;
};

export const useApolloClient = () => {
  const { apolloClient } = useApi();
  return apolloClient;
};

export const useKGPipelineApi = () => {
  const { kgPipelineApi } = useApi();
  return kgPipelineApi;
};

export const useParsedUploadApi = () => useApi().parsedUploadApi;

export const useAgentApi = () => useApi().agentApi;

export const useTeamsApi = () => useApi().teamsApi;

export const useDocumentApi = () => useApi().documentApi;

export const useSTTApi = () => useApi().sttApi;